import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import {
  SquareCardUpdateInfo,
  SquareCardUpdateResult,
  SquareEnvironment,
  SquareService,
  StripeCardUpdateInfo,
  StripeCardUpdateResult,
  StripeService,
} from '../api';
import { formatUsd } from '../orderPage/steps/payments/squarePaymentForm';
import {
  SQUARE_ERROR_TEXT_KEYS,
  SquareIntent,
  squareProviderErrorTextKey,
  tokenizeSquareCard,
  useSquareSdk,
} from '../orderPage/steps/payments/useSquareSdk';
import { StripePaymentFrame } from '../orderPage/steps/payments/StripePaymentFrame';
import { stripePublishableKey } from '../orderPage/steps/payments/useStripeRenderProps';
import RotatingLoader from '../ui/RotatingLoader';
import { CardUpdatedPage } from './CardUpdatedPage';

const GONE_STATUS = 410;
const STRIPE_PROVIDER = 'stripe';

const CardUpdateBox = ({ children }: { children: React.ReactNode }) => {
  const { i18n } = useTranslation();
  return (
    <Grid container justifyContent="center" dir={i18n.dir()} sx={{ px: { xs: 0, sm: 2 }, pt: { xs: 0, sm: 8 } }}>
      <Grid
        container
        flexDirection="column"
        gap={2}
        sx={{
          maxWidth: '550px',
          bgcolor: 'white',
          p: { xs: 2, sm: 4 },
          border: { xs: 0, sm: '1px solid rgba(0, 0, 0, 0.12)' },
          borderRadius: { xs: 0, sm: '12px' },
        }}>
        {children}
      </Grid>
    </Grid>
  );
};

const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Grid container justifyContent="space-between" flexWrap="nowrap" gap={2}>
    <Typography color="text.secondary">{label}</Typography>
    <Typography fontWeight={600}>{value}</Typography>
  </Grid>
);

type CardUpdateSummaryProps = {
  info: SquareCardUpdateInfo | StripeCardUpdateInfo;
};

const CardUpdateSummary = ({ info }: CardUpdateSummaryProps) => {
  const { t } = useTranslation();
  return (
    <>
      <Grid container flexDirection="column" gap={0.5}>
        <Typography variant="h5" fontWeight={700}>
          {t('card_update.title')}
        </Typography>
        <Typography color="text.secondary">
          {info.business_name} · {info.pass_label}
        </Typography>
      </Grid>
      {info.is_failing && <Alert severity="warning">{t('card_update.failedAlert')}</Alert>}
      <Grid container flexDirection="column" gap={1}>
        {info.card_suffix && <InfoRow label={t('card_update.cardOnFile')} value={<bdi>•••• {info.card_suffix}</bdi>} />}
        <InfoRow label={t('card_update.monthlyPrice')} value={<bdi>{formatUsd(info.monthly_price)}</bdi>} />
        {info.next_charge_date && (
          <InfoRow label={t('card_update.nextCharge')} value={moment(info.next_charge_date).format('LL')} />
        )}
      </Grid>
    </>
  );
};

const CardUpdateLinkInvalid = () => {
  const { t } = useTranslation();
  return (
    <CardUpdateBox>
      <Typography variant="h5" fontWeight={700}>
        {t('card_update.invalid.title')}
      </Typography>
      <Typography color="text.secondary">
        {t('card_update.invalid.body', { business: t('card_update.theBusiness') })}
      </Typography>
    </CardUpdateBox>
  );
};

const CardUpdateLoadFailed = ({ onRetry }: { onRetry: () => void }) => {
  const { t } = useTranslation();
  return (
    <CardUpdateBox>
      <Alert severity="error" role="alert">
        {t(SQUARE_ERROR_TEXT_KEYS.unavailable)}
      </Alert>
      <Button variant="contained" fullWidth onClick={onRetry}>
        {t('payment_form_error_retry')}
      </Button>
    </CardUpdateBox>
  );
};

type CardUpdateFormProps = {
  info: SquareCardUpdateInfo;
  token: string;
  onSaved: (result: SquareCardUpdateResult) => void;
  onLinkInvalid: () => void;
};

const CardUpdateForm = ({ info, token, onSaved, onLinkInvalid }: CardUpdateFormProps) => {
  const { t } = useTranslation();
  const [errorTextKey, setErrorTextKey] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const { cardContainerRef, card, hasLoadFailed } = useSquareSdk({
    environment: info.environment,
    applicationId: info.application_id,
    locationId: info.location_id,
  });

  const handleSave = async () => {
    if (!card) {
      return;
    }
    setIsSaving(true);
    setErrorTextKey(null);
    const tokenized = await tokenizeSquareCard(card, {
      intent: SquareIntent.STORE,
      billingContact: {
        givenName: info.customer_given_name,
        familyName: info.customer_family_name,
        email: info.customer_email,
      },
      customerInitiated: true,
      sellerKeyedIn: false,
    });
    if ('errorTextKey' in tokenized) {
      setErrorTextKey(tokenized.errorTextKey);
      setIsSaving(false);
      return;
    }
    try {
      onSaved(
        await SquareService.updateSquareCard({
          token,
          source_id: tokenized.token,
          idempotency_key: crypto.randomUUID().slice(0, 36),
        }),
      );
    } catch (e: any) {
      console.error(e);
      if (e?.status === GONE_STATUS) {
        onLinkInvalid();
        return;
      }
      setErrorTextKey(squareProviderErrorTextKey(e));
      setIsSaving(false);
    }
  };

  const shownErrorTextKey = hasLoadFailed ? SQUARE_ERROR_TEXT_KEYS.unavailable : errorTextKey;

  return (
    <CardUpdateBox>
      <CardUpdateSummary info={info} />
      <Typography variant="h6" fontWeight={600}>
        {t('card_update.newCard')}
      </Typography>
      {info.environment === SquareEnvironment.SANDBOX && (
        <Grid container flexDirection="column" gap={0.5}>
          <Typography color="error" fontWeight={700}>
            SANDBOX
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('order_page.square.sandboxHint')}
          </Typography>
        </Grid>
      )}
      {shownErrorTextKey && (
        <Alert severity="error" role="alert">
          {t(shownErrorTextKey)}
        </Alert>
      )}
      <Box ref={cardContainerRef} sx={{ minHeight: '96px' }} />
      <Button
        variant="contained"
        fullWidth
        disabled={isSaving || !card}
        onClick={() => {
          void handleSave();
        }}>
        {isSaving ? <CircularProgress size={24} /> : t('card_update.save')}
      </Button>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        {t('card_update.noCharge')}
      </Typography>
    </CardUpdateBox>
  );
};

const SquareCardUpdate = ({ token }: { token: string }) => {
  const [info, setInfo] = useState<SquareCardUpdateInfo | undefined>();
  const [saved, setSaved] = useState<SquareCardUpdateResult | undefined>();
  const [isLinkInvalid, setIsLinkInvalid] = useState(false);
  const [hasLoadFailed, setHasLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    void (async () => {
      try {
        setInfo(await SquareService.getSquareCardUpdate(token));
      } catch (e: any) {
        console.error(e);
        if (e?.status === GONE_STATUS) {
          setIsLinkInvalid(true);
        } else {
          setHasLoadFailed(true);
        }
      }
    })();
  }, [token, loadAttempt]);

  if (saved) {
    return (
      <CardUpdatedPage
        cardSuffix={saved.card_suffix}
        nextChargeDate={saved.next_charge_date ? moment(saved.next_charge_date).format('LL') : undefined}
      />
    );
  }

  if (isLinkInvalid) {
    return <CardUpdateLinkInvalid />;
  }

  if (hasLoadFailed) {
    return (
      <CardUpdateLoadFailed
        onRetry={() => {
          setHasLoadFailed(false);
          setLoadAttempt((attempt) => attempt + 1);
        }}
      />
    );
  }

  if (!info) {
    return <RotatingLoader />;
  }

  return <CardUpdateForm info={info} token={token} onSaved={setSaved} onLinkInvalid={() => setIsLinkInvalid(true)} />;
};

const StripeCardUpdate = ({ token }: { token: string }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [info, setInfo] = useState<StripeCardUpdateInfo | undefined>();
  const [saved, setSaved] = useState<StripeCardUpdateResult | undefined>();
  const [isLinkInvalid, setIsLinkInvalid] = useState(false);
  const [hasLoadFailed, setHasLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    void (async () => {
      try {
        if (sessionId) {
          setSaved(await StripeService.completeStripeCardUpdate({ token, session_id: sessionId }));
        } else {
          setInfo(await StripeService.getStripeCardUpdate(token));
        }
      } catch (e: any) {
        console.error(e);
        if (e?.status === GONE_STATUS) {
          setIsLinkInvalid(true);
        } else {
          setHasLoadFailed(true);
        }
      }
    })();
  }, [token, sessionId, loadAttempt]);

  if (saved) {
    return (
      <CardUpdatedPage
        cardSuffix={saved.card_suffix}
        nextChargeDate={saved.next_charge_date ? moment(saved.next_charge_date).format('LL') : undefined}
      />
    );
  }

  if (isLinkInvalid) {
    return <CardUpdateLinkInvalid />;
  }

  if (hasLoadFailed) {
    return (
      <CardUpdateLoadFailed
        onRetry={() => {
          setHasLoadFailed(false);
          setLoadAttempt((attempt) => attempt + 1);
        }}
      />
    );
  }

  if (!info) {
    return <RotatingLoader />;
  }

  const publishableKey = stripePublishableKey(info.is_test_mode);
  if (!publishableKey) {
    return <CardUpdateLoadFailed onRetry={() => setLoadAttempt((attempt) => attempt + 1)} />;
  }

  return (
    <CardUpdateBox>
      <CardUpdateSummary info={info} />
      <Typography variant="h6" fontWeight={600}>
        {t('card_update.newCard')}
      </Typography>
      <StripePaymentFrame
        publishableKey={publishableKey}
        clientSecret={info.client_secret}
        stripeAccountId={info.stripe_account_id}
      />
      <Typography variant="body2" color="text.secondary" textAlign="center">
        {t('card_update.noCharge')}
      </Typography>
    </CardUpdateBox>
  );
};

export const CardUpdatePage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  if (!token) {
    return <CardUpdateLinkInvalid />;
  }

  return searchParams.get('provider') === STRIPE_PROVIDER ? (
    <StripeCardUpdate token={token} />
  ) : (
    <SquareCardUpdate token={token} />
  );
};

export default CardUpdatePage;
